import { TFunction } from "i18next";
import { DataScienceJobOrigin } from "../../api/DataScienceJobApi";

export interface JobTypeConfig {
  key: string;
  label: string;
  jobType: string;
  origin: DataScienceJobOrigin;
  displayName: string;
  description: string;
  /** Whether this job type can be scheduled via cron. Defaults to true when omitted. */
  schedulable?: boolean;
  /** i18n key for an extra warning shown in the run confirmation (e.g. idempotent cleanup). */
  executeWarningKey?: string;
  /**
   * Whether this job type is a single, auto-provisioned system job per organization. When true, the
   * UI hides create/edit/delete affordances and only exposes run + history.
   */
  singletonSystemJob?: boolean;
}

export const getDataScienceJobTypes = (t: TFunction): JobTypeConfig[] => [
  {
    key: "demand_forecast",
    label: t("DataScienceJobs-JobTypes.demand_forecast"),
    jobType: "demand_forecast",
    origin: "data_science",
    displayName: t("DataScienceJobs-DemandForecastTitle"),
    description: t("DataScienceJobs-DemandForecastDescription"),
  },
  {
    key: "assortment_optimization",
    label: t("DataScienceJobs-JobTypes.assortment_optimization"),
    jobType: "assortment_optimization",
    origin: "data_science",
    displayName: t("DataScienceJobs-AssortmentOptimizationTitle"),
    description: t("DataScienceJobs-AssortmentOptimizationDescription"),
  },
  {
    key: "out_of_stock",
    label: t("DataScienceJobs-JobTypes.out_of_stock"),
    jobType: "out_of_stock",
    origin: "data_science",
    displayName: t("DataScienceJobs-OutOfStockTitle"),
    description: t("DataScienceJobs-OutOfStockDescription"),
  },
  {
    key: "restock",
    label: t("DataScienceJobs-JobTypes.restock"),
    jobType: "restock",
    origin: "data_science",
    displayName: t("DataScienceJobs-RestockTitle"),
    description: t("DataScienceJobs-RestockDescription"),
  },
  {
    key: "restock_manual_evaluation",
    label: t("DataScienceJobs-JobTypes.restock_manual_evaluation"),
    jobType: "restock_manual_evaluation",
    origin: "data_science",
    displayName: t("DataScienceJobs-RestockManualEvaluationTitle"),
    description: t("DataScienceJobs-RestockManualEvaluationDescription"),
  },
  {
    key: "segments",
    label: t("DataScienceJobs-JobTypes.segments"),
    jobType: "segments",
    origin: "data_science",
    displayName: t("DataScienceJobs-SegmentsTitle"),
    description: t("DataScienceJobs-SegmentsDescription"),
  },
  {
    key: "segment_engine",
    label: t("DataScienceJobs-JobTypes.segment_engine"),
    jobType: "segment_engine",
    origin: "api_ecs",
    displayName: t("DataScienceJobs-SegmentEngineTitle"),
    description: t("DataScienceJobs-SegmentEngineDescription"),
  },
  {
    key: "client_metrics_refresh",
    label: t("DataScienceJobs-JobTypes.client_metrics_refresh"),
    jobType: "client_metrics_refresh",
    origin: "api_ecs",
    displayName: t("DataScienceJobs-ClientMetricsRefreshTitle"),
    description: t("DataScienceJobs-ClientMetricsRefreshDescription"),
  },
  {
    key: "forecast_manual_promotions",
    label: t("DataScienceJobs-JobTypes.forecast_manual_promotions"),
    jobType: "forecast_manual_promotions",
    origin: "data_science",
    displayName: t("DataScienceJobs-ForecastManualPromotionsTitle"),
    description: t("DataScienceJobs-ForecastManualPromotionsDescription"),
  },
  {
    key: "promotions",
    label: t("DataScienceJobs-JobTypes.promotions"),
    jobType: "promotions",
    origin: "data_science",
    displayName: t("DataScienceJobs-PromotionsTitle"),
    description: t("DataScienceJobs-PromotionsDescription"),
    executeWarningKey: "DataScienceJobs-PromotionsExecuteWarning",
  },
  {
    key: "price_elasticity",
    label: t("DataScienceJobs-JobTypes.price_elasticity"),
    jobType: "price_elasticity",
    origin: "data_science",
    displayName: t("DataScienceJobs-PriceElasticityTitle"),
    description: t("DataScienceJobs-PriceElasticityDescription"),
  },
  {
    key: "price_cross_elasticity",
    label: t("DataScienceJobs-JobTypes.price_cross_elasticity"),
    jobType: "price_cross_elasticity",
    origin: "data_science",
    displayName: t("DataScienceJobs-PriceCrossElasticityTitle"),
    description: t("DataScienceJobs-PriceCrossElasticityDescription"),
  },
  {
    key: "seasonal_forecast",
    label: t("DataScienceJobs-JobTypes.seasonal_forecast"),
    jobType: "seasonal_forecast",
    origin: "data_science",
    displayName: t("DataScienceJobs-SeasonalForecastTitle"),
    description: t("DataScienceJobs-SeasonalForecastDescription"),
  },
  {
    key: "next_purchase",
    label: t("DataScienceJobs-JobTypes.next_purchase"),
    jobType: "next_purchase",
    origin: "data_science",
    displayName: t("DataScienceJobs-NextPurchaseTitle"),
    description: t("DataScienceJobs-NextPurchaseDescription"),
  },
  {
    key: "market_basket",
    label: t("DataScienceJobs-JobTypes.market_basket"),
    jobType: "market_basket",
    origin: "data_science",
    displayName: t("DataScienceJobs-MarketBasketTitle"),
    description: t("DataScienceJobs-MarketBasketDescription"),
  },
  {
    key: "store_transference",
    label: t("DataScienceJobs-JobTypes.store_transference"),
    jobType: "store_transference",
    origin: "data_science",
    displayName: t("DataScienceJobs-StoreTransferenceTitle"),
    description: t("DataScienceJobs-StoreTransferenceDescription"),
  },
  {
    key: "propensity_to_buy",
    label: t("DataScienceJobs-JobTypes.propensity_to_buy"),
    jobType: "propensity_to_buy",
    origin: "data_science",
    displayName: t("DataScienceJobs-PropensityToBuyTitle"),
    description: t("DataScienceJobs-PropensityToBuyDescription"),
  },
  {
    key: "rewards",
    label: t("DataScienceJobs-JobTypes.rewards"),
    jobType: "rewards",
    origin: "api",
    displayName: t("DataScienceJobs-RewardsTitle"),
    description: t("DataScienceJobs-RewardsDescription"),
    schedulable: false,
    executeWarningKey: "DataScienceJobs-RewardsExecuteWarning",
    singletonSystemJob: true,
  },
  {
    key: "client_elasticity",
    label: t("DataScienceJobs-JobTypes.client_elasticity"),
    jobType: "client_elasticity",
    origin: "data_science",
    displayName: t("DataScienceJobs-ClientElasticityTitle"),
    description: t("DataScienceJobs-ClientElasticityDescription"),
  },
  {
    key: "commercial_opportunities",
    label: t("DataScienceJobs-JobTypes.commercial_opportunities"),
    jobType: "commercial_opportunities",
    origin: "api",
    displayName: t("DataScienceJobs-CommercialOpportunitiesTitle"),
    description: t("DataScienceJobs-CommercialOpportunitiesDescription"),
    schedulable: false,
    executeWarningKey: "DataScienceJobs-CommercialOpportunitiesExecuteWarning",
    singletonSystemJob: true,
  },
  {
    key: "activation_actions",
    label: t("DataScienceJobs-JobTypes.activation_actions"),
    jobType: "activation_actions",
    origin: "api",
    displayName: t("DataScienceJobs-ActivationActionsTitle"),
    description: t("DataScienceJobs-ActivationActionsDescription"),
    schedulable: false,
    executeWarningKey: "DataScienceJobs-ActivationActionsExecuteWarning",
    singletonSystemJob: true,
  },
  {
    key: "demand_forecast_cleanup",
    label: t("DataScienceJobs-JobTypes.demand_forecast_cleanup"),
    jobType: "demand_forecast_cleanup",
    origin: "api",
    displayName: t("DataScienceJobs-DemandForecastCleanupTitle"),
    description: t("DataScienceJobs-DemandForecastCleanupDescription"),
    schedulable: false,
    executeWarningKey: "DataScienceJobs-DemandForecastCleanupExecuteWarning",
    singletonSystemJob: true,
  },
];

export const getJobTypeConfig = (
  t: TFunction,
  jobType: string
): JobTypeConfig | undefined =>
  getDataScienceJobTypes(t).find((item) => item.jobType === jobType);

const PROVISIONED_SINGLETON_JOB_SUFFIX: Record<string, string> = {
  commercial_opportunities: "commercial-opportunities",
  activation_actions: "activation-actions",
  rewards: "rewards",
  demand_forecast_cleanup: "demand-forecast-cleanup",
};

/** True when the job row is the auto-provisioned system job (org-{id}-{suffix}). */
export const isProvisionedSingletonSystemJob = (job: {
  job_name: string;
  job_type: string;
  organization_id: number;
}): boolean => {
  const suffix = PROVISIONED_SINGLETON_JOB_SUFFIX[job.job_type];
  if (!suffix) {
    return false;
  }
  return job.job_name === `org-${job.organization_id}-${suffix}`;
};

export const isKnownJobType = (jobType: string): boolean =>
  [
    "demand_forecast",
    "assortment_optimization",
    "out_of_stock",
    "restock",
    "restock_manual_evaluation",
    "segments",
    "segment_engine",
    "client_metrics_refresh",
    "forecast_manual_promotions",
    "promotions",
    "price_elasticity",
    "price_cross_elasticity",
    "seasonal_forecast",
    "next_purchase",
    "market_basket",
    "store_transference",
    "propensity_to_buy",
    "rewards",
    "client_elasticity",
    "commercial_opportunities",
    "activation_actions",
    "demand_forecast_cleanup",
  ].includes(jobType);

export const RECOMMENDATION_JOB_TYPES = [
  "segment_engine",
  "segments",
  "next_purchase",
  "rewards",
  "promotions",
] as const;

export type RecommendationJobType = (typeof RECOMMENDATION_JOB_TYPES)[number];

export const isRecommendationJobType = (jobType: string): jobType is RecommendationJobType =>
  (RECOMMENDATION_JOB_TYPES as readonly string[]).includes(jobType);
